import React, { useState } from 'react';
import { Download, Settings2, Plus, Check } from 'lucide-react';
import { toast } from 'sonner';
import API_URL from '@/config/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { fmtHours, textOn } from './utils';

export function SummaryPanel({ summary, showPeople, exportUrl }) {
  if (!summary) return null;
  const total = summary.total_minutes || 0;
  return (
    <Card className="p-4 space-y-4" data-testid="ts-summary">
      <div className="flex items-center justify-between gap-2">
        <div><div className="text-[11px] uppercase tracking-wider text-gray-500 font-bold">Total in view</div><div className="text-3xl font-black" data-testid="ts-summary-total">{fmtHours(total)}</div></div>
        <a href={exportUrl} className="inline-flex items-center gap-1.5 text-xs font-semibold border rounded-md px-3 py-2 hover:bg-gray-50" data-testid="ts-export-csv"><Download className="w-4 h-4" /> CSV</a>
      </div>
      <div>
        <div className="text-[11px] uppercase tracking-wider text-gray-500 font-bold mb-1.5">By job type</div>
        {summary.by_job_type.length === 0 && <div className="text-sm text-gray-400">Nothing logged in this range.</div>}
        <ul className="space-y-1.5">
          {summary.by_job_type.map((j) => (
            <li key={j.job_type_id} className="text-sm" data-testid={`ts-summary-job-${j.job_type_id}`}>
              <div className="flex justify-between"><span className="font-semibold truncate">{j.name}</span><span className="text-gray-700 font-bold">{fmtHours(j.minutes)}</span></div>
              <div className="h-1.5 rounded-full bg-gray-100 overflow-hidden"><div className="h-full rounded-full" style={{ width: `${total ? (j.minutes / total) * 100 : 0}%`, background: j.colour }} /></div>
            </li>
          ))}
        </ul>
      </div>
      {showPeople && summary.by_person.length > 0 && (
        <div>
          <div className="text-[11px] uppercase tracking-wider text-gray-500 font-bold mb-1.5">By person</div>
          <ul className="divide-y">
            {summary.by_person.map((p) => (
              <li key={p.user_id} className="py-1.5 flex justify-between text-sm" data-testid={`ts-summary-person-${p.user_id}`}>
                <span className="truncate"><span className="font-semibold">{p.name}</span> <span className="text-gray-400 text-xs">· {p.days} day{p.days === 1 ? '' : 's'}</span></span>
                <span className="font-bold">{fmtHours(p.minutes)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </Card>
  );
}

const PALETTE = ['#0f766e', '#c2410c', '#0369a1', '#a21caf', '#4d7c0f', '#b45309', '#6d28d9', '#475569', '#0e7490', '#be123c', '#1d4ed8', '#15803d'];

export function JobTypeManager({ jobTypes, onChanged }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [colour, setColour] = useState(PALETTE[0]);
  const add = async () => {
    if (!name.trim()) return;
    const r = await fetch(`${API_URL}/api/timesheets/job-types`, { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ name: name.trim(), colour, active: true }) });
    if (!r.ok) { toast.error('Could not add job type'); return; }
    setName(''); toast.success('Job type added'); onChanged();
  };
  const toggle = async (j) => {
    const r = await fetch(`${API_URL}/api/timesheets/job-types/${j.id}`, { method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ name: j.name, colour: j.colour, active: j.active === false }) });
    if (r.ok) onChanged(); else toast.error('Could not update');
  };
  return (
    <Card className="p-4" data-testid="ts-job-type-manager">
      <button type="button" onClick={() => setOpen((v) => !v)} className="w-full flex items-center justify-between text-sm font-bold" data-testid="ts-job-type-manager-toggle">
        <span className="inline-flex items-center gap-1.5"><Settings2 className="w-4 h-4" /> Job types</span><span className="text-xs text-gray-500">{jobTypes.filter((j) => j.active !== false).length} active</span>
      </button>
      {open && (
        <div className="mt-3 space-y-3">
          <ul className="space-y-1">
            {jobTypes.map((j) => (
              <li key={j.id} className="flex items-center gap-2 text-sm">
                <button type="button" onClick={() => toggle(j)} className={`w-5 h-5 rounded border flex items-center justify-center ${j.active === false ? 'bg-white' : ''}`} style={j.active === false ? {} : { background: j.colour, borderColor: j.colour }} aria-label={`Toggle ${j.name}`} data-testid={`ts-job-type-toggle-${j.id}`}>
                  {j.active !== false && <Check className="w-3.5 h-3.5" style={{ color: textOn(j.colour) }} />}
                </button>
                <span className={j.active === false ? 'text-gray-400 line-through' : ''}>{j.name}</span>
              </li>
            ))}
          </ul>
          <div className="flex gap-2 items-center">
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="New job type" className="h-9" data-testid="ts-job-type-name" onKeyDown={(e) => e.key === 'Enter' && add()} />
            <div className="flex gap-1">{PALETTE.slice(0, 6).map((c) => <button key={c} type="button" onClick={() => setColour(c)} className={`w-5 h-5 rounded-full ${colour === c ? 'ring-2 ring-offset-1 ring-gray-700' : ''}`} style={{ background: c }} aria-label={c} />)}</div>
            <Button size="sm" onClick={add} data-testid="ts-job-type-add"><Plus className="w-4 h-4" /></Button>
          </div>
        </div>
      )}
    </Card>
  );
}
